var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import InputNumber from "../inputs/input-number";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useApiCentral } from "../../../api/api-central";
import { useAlert } from "../../../services/alert";
const sortByName = (a, b) => a.queue_name.localeCompare(b.queue_name, undefined, { sensitivity: "base" });
const toDraft = (setting) => ({
    queue_number: setting.queue_number,
    queue_name: setting.queue_name,
    welcome_message_seconds: setting.welcome_message_seconds === null ? "" : String(setting.welcome_message_seconds),
    original: setting.welcome_message_seconds,
});
const parseSeconds = (value) => {
    const trimmed = value.trim();
    if (trimmed === "")
        return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0)
        return null;
    return Math.floor(parsed);
};
export default function ThreeCxQueuesModal({ open, onClose }) {
    const { t } = useTranslation();
    const api = useApiCentral();
    const alert = useAlert();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [loadFailed, setLoadFailed] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        setDrafts([]);
        setLoadFailed(false);
        setLoading(true);
        api.GET_centralThreeCxQueueSettings()
            .then(response => {
            if (cancelled)
                return;
            const sorted = [...response.data.data].sort(sortByName);
            setDrafts(sorted.map(toDraft));
        })
            .catch(error => {
            if (cancelled)
                return;
            console.error(error);
            setDrafts([]);
            setLoadFailed(true);
            alert.show(t("reports.3cx.queue-settings.load-error"), "error");
        })
            .finally(() => {
            if (cancelled)
                return;
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const draftKey = (draft) => `${draft.queue_number}|${draft.queue_name}`;
    const updateDraft = (key, value) => {
        setDrafts(prev => prev.map(draft => draftKey(draft) === key ? Object.assign(Object.assign({}, draft), { welcome_message_seconds: value }) : draft));
    };
    const dirty = useMemo(() => drafts.some(d => {
        if (d.welcome_message_seconds.trim() === "")
            return d.original !== null;
        const seconds = parseSeconds(d.welcome_message_seconds);
        return seconds !== d.original;
    }), [drafts]);
    const saveAll = () => __awaiter(this, void 0, void 0, function* () {
        for (const draft of drafts) {
            if (draft.welcome_message_seconds.trim() !== "" &&
                parseSeconds(draft.welcome_message_seconds) === null) {
                alert.show(t("reports.3cx.queue-settings.invalid-seconds"), "error");
                return;
            }
        }
        setSaving(true);
        try {
            const payload = drafts.map(draft => ({
                queue_number: draft.queue_number,
                queue_name: draft.queue_name,
                welcome_message_seconds: parseSeconds(draft.welcome_message_seconds),
            }));
            const response = yield api.SAVE_centralThreeCxQueueSettings({ settings: payload });
            const sorted = [...response.data.data].sort(sortByName);
            setDrafts(sorted.map(toDraft));
            alert.show(t("reports.3cx.queue-settings.saved"), "success");
        }
        catch (error) {
            console.error(error);
            alert.show(t("reports.3cx.queue-settings.save-error"), "error");
        }
        finally {
            setSaving(false);
        }
    });
    return (_jsxs(Dialog, Object.assign({ open: open, onClose: onClose, fullWidth: true, maxWidth: "sm" }, { children: [_jsx(DialogTitle, { children: t("reports.3cx.queue-settings.manage") }), _jsx(DialogContent, Object.assign({ dividers: true }, { children: loading ? (_jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "center", p: 4 } }, { children: _jsx(CircularProgress, {}) }))) : drafts.length === 0 ? (_jsx(Typography, Object.assign({ color: "text.secondary", sx: { textAlign: "center", p: 4 } }, { children: t("reports.3cx.queue-settings.empty") }))) : (_jsx(Grid, Object.assign({ container: true, spacing: 2 }, { children: drafts.map(draft => {
                        const key = draftKey(draft);
                        return (_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 2, alignItems: "center" }, { children: [_jsxs(Grid, Object.assign({ item: true, xs: 12, sm: 6 }, { children: [_jsx(Typography, Object.assign({ variant: "body1" }, { children: draft.queue_name })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: draft.queue_number }))] })), _jsx(Grid, Object.assign({ item: true, xs: 12, sm: 6 }, { children: _jsx(InputNumber, { label: t("reports.3cx.queue-settings.welcome-message-seconds"), required: false, onlyInteger: true, value: draft.welcome_message_seconds, setValue: value => updateDraft(key, value) }) }))] })) }), key));
                    }) }))) })), _jsx(DialogActions, Object.assign({ sx: { p: 3 } }, { children: _jsx(LoadingButton, Object.assign({ color: "secondary", variant: "contained", loading: saving, disabled: !dirty || loading || loadFailed, onClick: saveAll }, { children: t("reports.3cx.queue-settings.save") })) }))] })));
}
